import { DivisionMetadata } from "../../../models/ordering/enums/division";
import { OrderInvoiceStatusMetadata } from "../../../models/ordering/enums/order-invoice-status";
import { OrderProductionStatusMetadata } from "../../../models/ordering/enums/order-production-status";
import { OrderShipmentStatusMetadata } from "../../../models/ordering/enums/order-shipment-status";
import { OrderStatusMetadata } from "../../../models/ordering/enums/order-status";
import { AppliedFilterBarOpt, FilterBarInputOpt, FilterGroup, IFilterBar } from "../../../models/utilities/filter-bar";
import { ListColumn, SelectedSort } from "../../../models/utilities/list-header";
import { PageData } from "../../../models/utilities/paged-request";
import { ApiEndpoints, GlobalConstants } from "../../../utils/app-constants";
import { AppUtils } from "../../../utils/app-utils";

/**
 * Whether the applied filters narrow the search down at all.
 * (This is a function rather than a method because the filters get copied as the user edits them.)
 * @param filters The filters that have been applied
 */
export const hasSearchCriteria = (filters: IFilterBar) : boolean => {
    return filters.appliedFilters.length > 0;
};

export class OrderSearchFilters implements IFilterBar {

    public paginationInfo   : PageData = new PageData(GlobalConstants.DEFAULT_PAGE_SIZE);
    public baseUrl          : string = ApiEndpoints.ORDERING_ORDER_QUERY;
    public filters          : FilterGroup[] = [];
    public appliedFilters   : AppliedFilterBarOpt[] = [];
    public requiredFilters  : AppliedFilterBarOpt[] = [];
    public columns          : ListColumn[];
    public selectedSort     : SelectedSort;

    constructor() {

        // Set our table columns
        this.columns = [
            new ListColumn('Order ID', ''),
            new ListColumn('Status', ''),
            new ListColumn('Progress', ''),
            new ListColumn('Qty', ''),
            new ListColumn('Description', ''),
            new ListColumn('Invoice Group', ''),
            new ListColumn('Job', ''),
            new ListColumn('Total Amount', '')
        ];

        // Create our Filter Groups
        const byStatus      = this.buildStatus();
        const byProduction  = this.buildProductionStatus();
        const byShipment    = this.buildShipmentStatus();
        const byInvoiceStat = this.buildInvoiceStatus();
        const byDate        = this.buildCreateDate();
        const byOrder       = this.buildOrder();
        const byDealer      = this.buildDealer();
        const byDivision    = this.buildDivision();
        const byInvoice     = this.buildinvoiceGroup();
        const byJob         = this.buildJobName();

        // Only the first few filters show in the filter bar, the rest are in the "More" flyout
        this.filters.push(
            byDealer, byOrder, byInvoice, byJob, byDate, byStatus, byProduction, byShipment, byInvoiceStat, byDivision
        );

    }

    // #region Group Builders

    private buildCreateDate() {
        const byDate = new FilterGroup('Cutoff', 'date', 'cutoff');        
        const opt1 = new FilterBarInputOpt('Cutoff', undefined);
        
        byDate.opts = [opt1];

        return byDate;
    }

    private buildDealer() {
        const byDealer = new FilterGroup('Dealer', 'dealer', 'dealerId');        
        const opt1 = new FilterBarInputOpt('Dealer', '');
        
        byDealer.opts = [opt1];

        return byDealer;
    }

    private buildOrder() {
        const byOrderId = new FilterGroup('Order ID', 'text', 'id');
        const opt1 = new FilterBarInputOpt('Order ID', '');
        
        byOrderId.opts = [opt1];

        return byOrderId;
    }

    private buildinvoiceGroup() {
        const byInvoiceGroup = new FilterGroup('Invoice Group', 'text', 'invoiceGroup');
        const opt1 = new FilterBarInputOpt('Invoice Group', '');
        
        byInvoiceGroup.opts = [opt1];

        return byInvoiceGroup;
    }

    private buildJobName() {
        const byJob = new FilterGroup('Job/PO', 'text', 'jobName');
        const opt1 = new FilterBarInputOpt('Job/PO', '');
        
        byJob.opts = [opt1];

        return byJob;
    }
    
    private buildStatus() {
        const byStatus = new FilterGroup('Status', 'checkboxes', 'status');
        byStatus.opts = AppUtils.enumMetadataToFilters(OrderStatusMetadata);

        return byStatus;
    }

    private buildProductionStatus() {
        const byProduction = new FilterGroup('Production Status', 'checkboxes', 'productionStatus');
        byProduction.opts = AppUtils.enumMetadataToFilters(OrderProductionStatusMetadata);

        return byProduction;
    }

    private buildShipmentStatus() {
        const byShipment = new FilterGroup('Shipment Status', 'checkboxes', 'shipmentStatus');
        byShipment.opts = AppUtils.enumMetadataToFilters(OrderShipmentStatusMetadata);

        return byShipment;
    }

    private buildInvoiceStatus() {
        const byInvoiceStatus = new FilterGroup('Invoice Status', 'checkboxes', 'invoiceStatus');
        byInvoiceStatus.opts = AppUtils.enumMetadataToFilters(OrderInvoiceStatusMetadata);

        return byInvoiceStatus;
    }

    private buildDivision() {
        const byDivision = new FilterGroup('Division', 'checkboxes', 'division');
        byDivision.opts = AppUtils.enumMetadataToFilters(DivisionMetadata);
        
        return byDivision;
    }    

    // #endregion

}
